<template>
  <div class="calculator" data-cy="price-calculator-modal">
    <SDialog v-model:open="dialogVisible">
      <SDialogContent
        data-cy="price-calculator-dialog"
        class="price-calculator !flex !flex-col !w-[340px] !max-w-[340px] !top-[22vh] !translate-y-0 !gap-0 !p-0 overflow-hidden"
        :show-close-button="false"
        @escape-key-down.prevent
        @pointer-down-outside.prevent
        @open-auto-focus.prevent
      >
        <SDialogHeader>
          <SDialogTitle class="sr-only">
            {{ $t("calculator.insert-amount") }}
          </SDialogTitle>
        </SDialogHeader>

        <!-- Amount display -->
        <div class="px-5 pt-5 pb-4">
          <p class="mb-2 text-[13px] font-medium text-muted-foreground">
            {{ $t("calculator.insert-amount") }}
          </p>
          <div
            class="flex h-16 items-center gap-2 rounded-lg border border-input pr-1 pl-4"
          >
            <span
              v-if="currentCurrency"
              class="shrink-0 text-[22px] font-medium text-muted-foreground"
            >
              {{ currentCurrency }}
            </span>
            <input
              class="min-w-0 flex-1 bg-transparent text-right text-[30px] font-semibold tabular-nums outline-none"
              :value="inputContent"
              readonly
              inputmode="none"
              data-cy="price-calculator-display"
            />
            <SButton
              type="button"
              variant="ghost"
              size="icon"
              class="size-12 shrink-0 text-muted-foreground"
              aria-label="erase"
              @keydown.stop="eraseFromInput"
              @click="eraseFromInput()"
            >
              <!-- size-7 escapes SButton's svg clamp; --s-icon-size is what .s-icon sizes by -->
              <DeleteIcon class="size-7" style="--s-icon-size: 30px" />
            </SButton>
          </div>
        </div>

        <!-- Keypad + Cancel -->
        <div class="flex gap-px border-t border-border bg-border">
          <div class="grid flex-1 grid-cols-3 gap-px bg-border">
            <SButton
              v-for="digit in 9"
              :key="digit"
              type="button"
              variant="ghost"
              class="calc-key"
              :data-cy="`calc-key-${digit}`"
              @click="updateInput(String(digit))"
            >
              {{ digit }}
            </SButton>
            <SButton
              type="button"
              variant="ghost"
              class="calc-key"
              data-cy="calc-key-0"
              @click="updateZero"
            >
              0
            </SButton>
            <SButton
              type="button"
              variant="ghost"
              class="calc-key"
              data-cy="calc-key-00"
              @click="updateInputDoubleZero"
            >
              00
            </SButton>
            <SButton
              type="button"
              variant="ghost"
              class="calc-key calc-key--clear"
              data-cy="calc-key-clear"
              @click="clearInput"
            >
              {{ $t("calculator.clear") }}
            </SButton>
          </div>

          <SButton
            type="button"
            variant="ghost"
            class="calc-key calc-key--cancel"
            data-cy="price-calculator-cancel-button"
            @click="cancel()"
          >
            {{ $t("button.cancel") }}
          </SButton>
        </div>

        <!-- Confirm -->
        <SButton
          type="button"
          class="confirm-button"
          data-cy="price-calculator-confirm-button"
          @click="$emit('confirmNumber', Number(inputContent))"
        >
          {{ $t("calculator.confirm-price") }}
        </SButton>
      </SDialogContent>
    </SDialog>
  </div>
</template>

<script lang="ts">
import { ModalComponents, ModalStatus } from "@/constants/modal";
import ModalModule from "@/store/modules/modal";
import { useOrderStore } from "@smeetz/order-store";
import { dialogVisibleForStatus } from "@/utils/vue-helpers/modal";
import { computed, defineComponent, PropType, ref } from "vue";
import {
  SButton,
  SDialog,
  SDialogContent,
  SDialogHeader,
  SDialogTitle,
} from "@smeetz/ui-core";
import { DeleteIcon } from "@smeetz/ui-icons";

export default defineComponent({
  name: ModalComponents.Calculator,
  components: {
    SButton,
    SDialog,
    SDialogContent,
    SDialogHeader,
    SDialogTitle,
    DeleteIcon,
  },
  props: {
    visible: {
      required: false,
      type: Object as PropType<number>,
    },
    placeHolderValue: {
      required: false,
      type: Object as PropType<number>,
    },
  },
  emits: [
    "confirmNumber" /* this event is declared in the parent component Modal*/,
    "cancelCalculator",
  ],

  setup(props) {
    let dialogVisible = ref(
      props.visible
        ? props.visible
        : dialogVisibleForStatus(ModalStatus.Calculator)
    );
    // Cash-register input, same as the payment pad: digits shift in from the
    // right and the decimal point is fixed. The placeholder (current price) is
    // display-only; the first keypress starts a fresh amount.
    let inputContent = props.placeHolderValue
      ? ref(props.placeHolderValue.toFixed(2))
      : ref("0.00");
    let initialArray = ["0", "0", "0"];
    let count = 0;
    const orderStore = useOrderStore();
    const currentCurrency = computed(() => {
      return orderStore.selectedOrder?.orderSession?.currency;
    });

    function clearInput() {
      inputContent.value = "0.00";
      count = 0;
      initialArray = ["0", "0", "0"];
    }

    function updateInputDoubleZero() {
      if (count >= 1) {
        updateInput(String(0));
        updateInput(String(0));
      }
      if (count === 0) {
        return;
      }
    }

    function updateZero() {
      if (count >= 1) {
        updateInput(String(0));
      }
      if (count === 0) {
        return;
      }
    }

    function eraseFromInput() {
      if (count === 1 || count === 0) {
        inputContent.value = "0.00";
        count = 0;
        initialArray = ["0", "0", "0"];
      } else if (count === 2) {
        count = count - 1;
        initialArray.pop();
        initialArray.unshift("0");
        inputContent.value = `${initialArray[0]}.${initialArray[1]}${initialArray[2]}`;
      } else if (count === 3) {
        count = count - 1;
        initialArray.pop();
        initialArray.unshift("0");
        inputContent.value = `${initialArray[0]}.${initialArray[1]}${initialArray[2]}`;
      } else if (count >= 4) {
        count = count - 1;
        initialArray.pop();
        const decimalDivider = initialArray.length - 2;
        inputContent.value =
          initialArray.slice(0, decimalDivider).join("") +
          "." +
          initialArray.slice(decimalDivider, decimalDivider + 2).join("");
      }
    }

    function updateInput(char: string) {
      if (count === 0) {
        inputContent.value = `${initialArray[0]}.${initialArray[1]}${char}`;
        initialArray.push(char);
        initialArray.shift();
        count = 1;
      } else if (count === 1) {
        inputContent.value = `${initialArray[0]}.${initialArray[2]}${char}`;
        initialArray.push(char);
        initialArray.shift();
        count = 2;
      } else if (count === 2) {
        inputContent.value = `${initialArray[1]}.${initialArray[2]}${char}`;
        initialArray.push(char);
        initialArray.shift();
        count = 3;
      } else {
        initialArray.push(char);
        count = count + 1;
        const decimalDivider = initialArray.length - 2;
        inputContent.value =
          initialArray.slice(0, decimalDivider).join("") +
          "." +
          initialArray.slice(decimalDivider, decimalDivider + 2).join("");
      }
    }

    function cancel() {
      // Clear the calculator and close it
      clearInput();
      if (ModalModule.status === ModalStatus.Calculator) {
        ModalModule.setStatus(ModalStatus.Hidden);
      } else {
        // this is the case where the calculator is not used inside Modal component, lets emit to cancellation event on parent component
        this.$emit("cancelCalculator");
      }
    }

    return {
      dialogVisible,
      inputContent,
      currentCurrency,
      clearInput,
      eraseFromInput,
      updateInput,
      updateZero,
      updateInputDoubleZero,
      cancel,
      props,
    };
  },
  methods: {},
});
</script>

<style lang="scss" scoped>
@use "@/styles/variables.scss" as *;

// Keypad keys: flush, square, white with hover/pressed states, matching the
// payment calculator. The 1px separators come from the gap-px + bg-border wrappers
.calc-key {
  height: 62px;
  border-radius: 0;
  background-color: #fff;
  color: $color-black-3;
  font-size: 24px;
  font-weight: 400;

  &:hover {
    background-color: #f5f6f7;
  }

  &:active {
    background-color: #eceef0;
  }
}

.calc-key--clear {
  font-size: 16px;
  font-weight: 500;
  color: $color-green2;
}

.calc-key--cancel {
  width: 84px;
  height: auto;
  align-self: stretch;
  font-size: 16px;
  font-weight: 500;
  color: $color-green2;
}

.confirm-button {
  width: 100%;
  height: 60px;
  border-radius: 0;
  background-color: $color-3;
  color: $background-color-clear;
  font-weight: 700;
  font-size: 18px;

  &:hover {
    background-color: $color-3;
    filter: brightness(0.95);
  }
}
</style>
